
import React, { useState, useEffect } from 'react';
import { Modal } from "react-bootstrap";
import axios from "axios";
import config from '../../Common/Configurations/APIConfig';
import AdminHeader from '../../Common/Layout/AdminHeader';
import { rexTechTheme } from '../../Common/Theme';
import {
    Home,
    Calendar,
    Edit2,
    Trash2,
    Plus,
    Search,
    Filter,
    CheckCircle,
    XCircle,
    AlertCircle,
    MapPin,
    Users,
    Package,
    Truck,
    RefreshCw,
    Eye,
    Mail,
    Phone,
    User,
    Building,
    BarChart,
    FileText,
    Key,
    Hash
} from 'lucide-react';

// Initial form values
const initialFieldValues = {
    requestId: '00000000-0000-0000-0000-000000000000',
    userId: '',
    pickupDate: getDefaultDateTime(),
    pickupAddress: '',
    notes: '',
    status: 'NEW',
    requestItems: []
};

// Initial item values
const initialItemValues = {
    requestItemId: '00000000-0000-0000-0000-000000000000',
    pickupItemId: '',
    pallet: '',
    weight: ''
};

// Get current date-time in the correct format for datetime-local input
function getDefaultDateTime() {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    return now.toISOString().slice(0, 16);
}

// Get today's date in YYYY-MM-DD format for filters
function getTodayDate() {
    return new Date().toISOString().split('T')[0];
}

const statusOptions = [
    {
        value: 'NEW',
        label: 'NEW',
        color: rexTechTheme.colors.primary.main,
        bgColor: rexTechTheme.colors.primary.lightest
    },
    {
        value: 'COLLECTOR ASSIGNED',
        label: 'COLLECTOR ASSIGNED',
        color: rexTechTheme.colors.accent.blue,
        bgColor: `${rexTechTheme.colors.accent.blue}20`
    },
    {
        value: 'COLLECTED',
        label: 'COLLECTED',
        color: rexTechTheme.colors.accent.teal,
        bgColor: `${rexTechTheme.colors.accent.teal}20`
    },
    {
        value: 'DELIVERED',
        label: 'DELIVERED',
        color: rexTechTheme.colors.primary.main,
        bgColor: rexTechTheme.colors.primary.lightest
    },
    {
        value: 'RECYCLED',
        label: 'RECYCLED',
        color: rexTechTheme.colors.accent.orange,
        bgColor: `${rexTechTheme.colors.accent.orange}20`
    }
];
const AdminRequests = () => {
    const [requests, setRequests] = useState([]);
    const [filteredRequests, setFilteredRequests] = useState([]);
    const [users, setUsers] = useState([]);
    const [collectors, setCollectors] = useState([]);
    const [pickupItems, setPickupItems] = useState([]);
    const [btnLoading, setBtnLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showViewModal, setShowViewModal] = useState(false);
    const [requestToDelete, setRequestToDelete] = useState(null);
    const [selectedRequest, setSelectedRequest] = useState(null);
    const [values, setValues] = useState(initialFieldValues);
    const [itemValues, setItemValues] = useState(initialItemValues);
    const [errors, setErrors] = useState({});
    const [itemErrors, setItemErrors] = useState({});
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('All');
    const [dateRange, setDateRange] = useState({
        from: getTodayDate(),
        to: getTodayDate()
    });
    const [isLoading, setIsLoading] = useState(true);
    const [expandedRequestId, setExpandedRequestId] = useState(null);
    const [dashboardStats, setDashboardStats] = useState(null);

    // Assign collector states
    const [selectedCollectorId, setSelectedCollectorId] = useState('');
    const [truckReferenceNumber, setTruckReferenceNumber] = useState('');
    const [sealNumber, setSealNumber] = useState('');
    const [assignErrors, setAssignErrors] = useState({});

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalItems, setTotalItems] = useState(0);

    // Notes state for assign collector
    const [notes, setNotes] = useState('');
    // Selected images state
    const [selectedImages, setSelectedImages] = useState([]);

    useEffect(() => {
        GetData();
        GetDashboardStats();
        GetCollectors();
        GetPickupItems();
    }, []);

    useEffect(() => {
        filterRequests();
    }, [searchTerm, statusFilter, dateRange, requests]);

    useEffect(() => {
        // Reset to page 1 when filters change
        setCurrentPage(1);
    }, [searchTerm, statusFilter, dateRange]);

    // Reset assign form when modal opens/closes
    useEffect(() => {
        if (showAssignModal && selectedRequest) {
            // Check if collector details exist
            if (selectedRequest.collectorDetails) {
                setSelectedCollectorId(selectedRequest.collectorId || '');
                setTruckReferenceNumber(selectedRequest.collectorDetails.truckReferenceNumber || '');
                setSealNumber(selectedRequest.collectorDetails.sealNumber || '');
                setNotes(selectedRequest.collectorDetails.notes || '');
            } else {
                setSelectedCollectorId(selectedRequest.collectorId || '');
                setTruckReferenceNumber('');
                setSealNumber('');
                setNotes('');
            }
            setSelectedImages([]);
            setAssignErrors({});
        }
    }, [showAssignModal, selectedRequest]);

    // Calculate pagination
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentRequests = filteredRequests.slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);

    // ================= Helper Functions =================

    const getNextStatus = (currentStatus) => {
        switch (currentStatus) {
            case 'NEW':
                return 'COLLECTOR ASSIGNED';
            case 'COLLECTOR ASSIGNED':
                return 'COLLECTED';
            case 'COLLECTED':
                return 'DELIVERED';
            case 'DELIVERED':
                return 'RECYCLED';
            default:
                return null;
        }
    };

    const getStatusConfirmationMessage = (currentStatus, nextStatus) => {
        const messages = {
            'NEW': {
                title: 'Assign Collector?',
                message: 'Are you sure you want to assign a collector to this request? This will change the status to "COLLECTOR ASSIGNED".',
                confirmText: 'Yes, Assign Collector',
                cancelText: 'No, Cancel'
            },
            'COLLECTOR ASSIGNED': {
                title: 'Mark as Collected?',
                message: 'Are you sure you want to mark this request as collected? This will change the status to "COLLECTED".',
                confirmText: 'Yes, Mark as Collected',
                cancelText: 'No, Cancel'
            },
            'COLLECTED': {
                title: 'Mark as Delivered?',
                message: 'Are you sure you want to mark this request as delivered? This will change the status to "DELIVERED".',
                confirmText: 'Yes, Mark as Delivered',
                cancelText: 'No, Cancel'
            },
            'DELIVERED': {
                title: 'Mark as Recycled?',
                message: 'Are you sure you want to mark this request as recycled? This will change the status to "RECYCLED".',
                confirmText: 'Yes, Mark as Recycled',
                cancelText: 'No, Cancel'
            }
        };

        return messages[currentStatus] || {
            title: 'Confirm Status Change',
            message: `Are you sure you want to change status from "${currentStatus}" to "${nextStatus}"?`,
            confirmText: 'Yes, Change Status',
            cancelText: 'No, Cancel'
        };
    };

    // ================= API Calls =================

    const GetData = async () => {
        try {
            setIsLoading(true);
            const url = config.APIACTIVEURL + config.GETALLREQUESTS;
            console.log('Fetching all requests from:', url);

            const res = await axios.get(url);
            console.log('All requests response:', res.data);

            if (res.data && res.data.data) {
                setRequests(res.data.data || []);
                setTotalItems(res.data.totalRecords || 0);
            } else {
                setRequests(res.data || []);
            }
        } catch (error) {
            console.error("Error fetching requests", error);
            showToast('error', 'Failed to load requests: ' + error.message);
        } finally {
            setIsLoading(false);
        }
    };

    const GetDashboardStats = async () => {
        try {
            const res = await axios.get(config.APIACTIVEURL + 'Request/DashboardStats');
            setDashboardStats(res.data.data || {});
        } catch (error) {
            console.error("Error fetching dashboard stats", error);
        }
    };

 

    const GetCollectors = async () => {
        try {
           const res = await axios.get(`${config.APIACTIVEURL}${config.GETALLCOMPANIES}?companyType=COLLECTOR`);
            setCollectors(res.data || []);
        } catch (error) {
            console.error("Error fetching collectors", error);
            showToast('error', 'Failed to load collectors');
        }
    };

    const GetPickupItems = async () => {
        try {
            const res = await axios.get(config.APIACTIVEURL + config.GETALLPICKUPITEMS);
            setPickupItems(res.data || []);
        } catch (error) {
            console.error("Error fetching pickup items", error);
            showToast('error', 'Failed to load pickup items');
        }
    };

    const showToast = (type, message) => {
        alert(`${type.toUpperCase()}: ${message}`);
        console.log(`${type}: ${message}`);
    };

    // ================= Filtering =================

    const filterRequests = () => {
        let filtered = [...requests];

        // Search filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(request =>
                request.pickupAddress?.toLowerCase().includes(term) ||
                request.userName?.toLowerCase().includes(term) ||
                request.userEmail?.toLowerCase().includes(term) ||
                request.notes?.toLowerCase().includes(term) ||
                request.collectorName?.toLowerCase().includes(term)
            );
        }

        // Status filter
        if (statusFilter !== 'All') {
            filtered = filtered.filter(request => request.status === statusFilter);
        }

        // Date range filter
        if (dateRange.from) {
            const fromDate = new Date(dateRange.from);
            fromDate.setHours(0, 0, 0, 0);
            filtered = filtered.filter(request => {
                const requestDate = new Date(request.pickupDate);
                return requestDate >= fromDate;
            });
        }

        if (dateRange.to) {
            const toDate = new Date(dateRange.to);
            toDate.setHours(23, 59, 59, 999);
            filtered = filtered.filter(request => {
                const requestDate = new Date(request.pickupDate);
                return requestDate <= toDate;
            });
        }

        setFilteredRequests(filtered);
    };

    // ================= Form Handling =================

    const handleInput = (e) => {
        const { name, value } = e.target;
        setValues({ ...values, [name]: value });
        if (errors[name]) {
            setErrors({ ...errors, [name]: '' });
        }
    };

    const handleItemInput = (e) => {
        const { name, value } = e.target;
        setItemValues({ ...itemValues, [name]: value });
        if (itemErrors[name]) {
            setItemErrors({ ...itemErrors, [name]: '' });
        }
    };

    const validate = () => {
        let temp = {};

        // Required field validations
        temp.userId = values.userId ? "" : "Please select a user";
        temp.pickupDate = values.pickupDate ? "" : "Pickup date is required";
        temp.pickupAddress = values.pickupAddress ? "" : "Pickup address is required";

        // Length validations
        if (values.pickupAddress && values.pickupAddress.length > 500) {
            temp.pickupAddress = "Pickup address must not exceed 500 characters";
        }

        if (values.notes && values.notes.length > 1000) {
            temp.notes = "Notes must not exceed 1000 characters";
        }

        // Request items validation
        if (values.requestItems.length === 0) {
            temp.requestItems = "At least one pickup item is required";
        }

        setErrors(temp);
        return Object.values(temp).every(x => x === "");
    };

    const validateItem = () => {
        let temp = {};

        temp.pickupItemId = itemValues.pickupItemId ? "" : "Pickup item is required";

        if (itemValues.pallet && (itemValues.pallet < 0 || itemValues.pallet > 1000)) {
            temp.pallet = "Pallet must be between 0 and 1000";
        }

        if (itemValues.weight && (itemValues.weight < 0 || itemValues.weight > 10000)) {
            temp.weight = "Weight must be between 0 and 10000";
        }

        setItemErrors(temp);
        return Object.values(temp).every(x => x === "");
    };

    // ================= Request Items Management =================

    const addRequestItem = () => {
        if (!validateItem()) return;

        const newItem = {
            ...itemValues,
            requestItemId: itemValues.requestItemId === '00000000-0000-0000-0000-000000000000'
                ? null
                : itemValues.requestItemId
        };

        if (itemValues.requestItemId === '00000000-0000-0000-0000-000000000000') {
            setValues({
                ...values,
                requestItems: [...values.requestItems, newItem]
            });
        } else {
            const updatedItems = values.requestItems.map(item =>
                item.requestItemId === itemValues.requestItemId ? newItem : item
            );
            setValues({ ...values, requestItems: updatedItems });
        }

        setItemValues(initialItemValues);
        setItemErrors({});
    };

    const editRequestItem = (item) => {
        setItemValues({
            requestItemId: item.requestItemId || '00000000-0000-0000-0000-000000000000',
            pickupItemId: item.pickupItemId,
            pallet: item.pallet || '',
            weight: item.weight || ''
        });
    };

    const removeRequestItem = (index) => {
        const updatedItems = [...values.requestItems];
        updatedItems.splice(index, 1);
        setValues({ ...values, requestItems: updatedItems });
    };

    // ================= CRUD Operations =================

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setBtnLoading(true);

        try {
            const isNew = values.requestId === '00000000-0000-0000-0000-000000000000';
            const url = config.APIACTIVEURL + config.CREATEREQUEST;

            // Format data to match backend RequestRequest model
            const dataToSend = {
                userId: values.userId,
                pickupDate: new Date(values.pickupDate).toISOString(),
                pickupAddress: values.pickupAddress.trim(),
                notes: values.notes?.trim() || null,
                status: values.status || 'NEW',
                requestItems: values.requestItems.map(item => ({
                    pickupItemId: item.pickupItemId,
                    pallet: item.pallet ? parseFloat(item.pallet) : null,
                    weight: item.weight ? parseFloat(item.weight) : null
                }))
            };

            console.log('Sending data to:', url);
            console.log('Request data:', dataToSend);

            // For POST requests (new requests)
            if (isNew) {
                const response = await axios.post(url, dataToSend, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                console.log('Response:', response.data);

                if (response.data && response.data.succeeded) {
                    showToast('success', 'Request created successfully');
                    setShowModal(false);
                    resetForm();
                    GetData();
                    GetDashboardStats();
                } else {
                    showToast('error', response.data?.message || 'Failed to create request');
                }
            }
            // For PUT requests (updating existing)
            else {
                // For update, include requestId in the data
                const updateData = {
                    ...dataToSend,
                    requestId: values.requestId
                };

                const response = await axios.put(config.APIACTIVEURL + config.UPDATEREQUEST, updateData, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                });

                if (response.data && response.data.succeeded) {
                    showToast('success', 'Request updated successfully');
                    setShowModal(false);
                    resetForm();
                    GetData();
                    GetDashboardStats();
                } else {
                    showToast('error', response.data?.message || 'Failed to update request');
                }
            }
        } catch (error) {
            console.error("Save failed", error);

            if (error.response) {
                console.error("Error response:", error.response.data);
                showToast('error', error.response.data?.message || `Failed to ${values.requestId === '00000000-0000-0000-0000-000000000000' ? 'create' : 'update'} request`);
            } else if (error.request) {
                console.error("No response received:", error.request);
                showToast('error', 'No response from server. Please check your connection.');
            } else {
                console.error("Error:", error.message);
                showToast('error', error.message || 'An unexpected error occurred');
            }
        } finally {
            setBtnLoading(false);
        }
    };

    const handleEdit = (request) => {
        setValues({
            requestId: request.requestId,
            userId: request.userId,
            pickupDate: new Date(request.pickupDate).toISOString().slice(0, 16),
            pickupAddress: request.pickupAddress,
            notes: request.notes || '',
            status: request.status,
            requestItems: request.requestItems || []
        });
        setErrors({});
        setShowModal(true);
    };

    const handleView = (request) => {
        setSelectedRequest(request);
        setShowViewModal(true);
    };

    const handleDelete = async () => {
        try {
            await axios.delete(config.APIACTIVEURL + config.DELETEREQUEST + '/' + requestToDelete);
            showToast('success', 'Request deleted successfully');
            setShowDeleteModal(false);
            GetData();
            GetDashboardStats();
        } catch (error) {
            console.error("Delete failed", error);
            showToast('error', 'Failed to delete request');
        }
    };

    const updateStatus = async (newStatus) => {
        try {
            await axios.put(config.APIACTIVEURL + config.UPDATESTATUS, {
                requestId: selectedRequest.requestId,
                status: newStatus,
                collectorId: selectedRequest.collectorId
            });
            showToast('success', 'Status updated successfully');
            setShowStatusModal(false);
            GetData();
            GetDashboardStats();
        } catch (error) {
            console.error("Status update failed", error);
            showToast('error', 'Failed to update status');
        }
    };

    const handleStatusButtonClick = (request) => {
        setSelectedRequest(request);

        const nextStatus = getNextStatus(request.status);

        if (!nextStatus) {
            showToast('info', 'This request has reached the final status');
            return;
        }

        if (request.status === 'NEW') {
            // For NEW status, show the assign collector modal instead of status modal
            setShowAssignModal(true);
        } else {
            // For other statuses, show confirmation dialog
            setShowStatusModal(true);
        }
    };

    // ================= Assign Collector Functions =================

    const validateAssignForm = () => {
        let temp = {};

        // Collector is required
        if (!selectedCollectorId) {
            temp.collectorId = "Please select a collector";
        }

        // Truck Reference Number is required
        if (!truckReferenceNumber.trim()) {
            temp.truckReferenceNumber = "Truck Reference Number is required";
        } else if (truckReferenceNumber.length > 50) {
            temp.truckReferenceNumber = "Truck Reference Number must not exceed 50 characters";
        }

        // Seal Number is required
        if (!sealNumber.trim()) {
            temp.sealNumber = "Seal Number is required";
        } else if (sealNumber.length > 50) {
            temp.sealNumber = "Seal Number must not exceed 50 characters";
        }

        setAssignErrors(temp);
        return Object.values(temp).every(x => x === "");
    };

    // Helper function to convert file to base64
    const convertFileToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    };

    const assignCollector = async () => {
        if (!validateAssignForm()) return;

        try {
            setBtnLoading(true);

            const payload = {
                requestId: selectedRequest.requestId,
                collectorId: selectedCollectorId,
                truckReferenceNumber: truckReferenceNumber.trim(),
                sealNumber: sealNumber.trim(),
                notes: notes.trim() || null,
                images: []
            };

            // Convert selected images to base64
            if (selectedImages.length > 0) {
                const convertedImages = await Promise.all(
                    selectedImages.map(async (image) => {
                        try {
                            const base64 = await convertFileToBase64(image);
                            return {
                                base64Image: base64,
                                imageType: "Document",
                                fileName: image.name,
                                description: image.name
                            };
                        } catch (error) {
                            console.error("Error converting image to base64:", error);
                            return null;
                        }
                    })
                );

                // Filter out any failed conversions
                payload.images = convertedImages.filter(img => img !== null);
            }

            console.log('Assigning collector with payload:', payload);

            await axios.put(config.APIACTIVEURL + config.UPDATECOLLECTOR, payload);



            showToast('success', 'Collector assigned and status updated successfully');
            setShowAssignModal(false);
            setSelectedImages([]);
            GetData();
            GetDashboardStats();
        } catch (error) {
            console.error("Assign collector failed", error);
            showToast('error', 'Failed to assign collector');
        } finally {
            setBtnLoading(false);
        }
    };

    // Image handling functions
    const handleImageUpload = (e) => {
        const files = Array.from(e.target.files);

        // Validate file types
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
        const invalidFiles = files.filter(file => !validTypes.includes(file.type));

        if (invalidFiles.length > 0) {
            showToast('error', 'Only JPG, PNG, and GIF images are allowed');
            return;
        }

        // Validate file sizes (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        const oversizedFiles = files.filter(file => file.size > maxSize);

        if (oversizedFiles.length > 0) {
            showToast('error', 'Images must be less than 5MB');
            return;
        }

        setSelectedImages(prev => [...prev, ...files]);
    };

    const removeImage = (index) => {
        setSelectedImages(prev => prev.filter((_, i) => i !== index));
    };

    const unassignCollector = async () => {
        try {
            setBtnLoading(true);

            const payload = {
                requestId: selectedRequest.requestId,
                collectorId: '',
                truckReferenceNumber: '',
                sealNumber: '',
                notes: '',
                images: []
            };

            await axios.put(config.APIACTIVEURL + config.UPDATECOLLECTOR, payload);

            showToast('success', 'Collector unassigned successfully');
            setShowAssignModal(false);
            // Reset form
            setSelectedImages([]);
            GetData();
            GetDashboardStats();
        } catch (error) {
            console.error("Unassign collector failed", error);
            showToast('error', 'Failed to unassign collector');
        } finally {
            setBtnLoading(false);
        }
    };

    const resetForm = () => {
        setValues(initialFieldValues);
        setItemValues(initialItemValues);
        setErrors({});
        setItemErrors({});
    };

    // ================= Helper Functions =================

    const getPickupItemName = (pickupItemId) => {
        const item = pickupItems.find(p => p.pickupItemId === pickupItemId);
        return item ? item.pickupItemName : 'Unknown Item';
    };

    const getStatusBadge = (status) => {
        const statusOption = statusOptions.find(s => s.value === status);
        if (!statusOption) {
            return (
                <span style={{
                    padding: `${rexTechTheme.spacing.xs} ${rexTechTheme.spacing.md}`,
                    backgroundColor: rexTechTheme.colors.neutral.lightGray,
                    color: rexTechTheme.colors.neutral.darkGray,
                    borderRadius: rexTechTheme.borderRadius.small,
                    fontSize: rexTechTheme.typography.fontSize.xs,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                }}>
                    {status || 'UNKNOWN'}
                </span>
            );
        }

        return (
            <span style={{
                padding: `${rexTechTheme.spacing.xs} ${rexTechTheme.spacing.md}`,
                backgroundColor: statusOption.bgColor,
                color: statusOption.color,
                borderRadius: rexTechTheme.borderRadius.small,
                fontSize: rexTechTheme.typography.fontSize.xs,
                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                display: 'inline-flex',
                alignItems: 'center',
                gap: rexTechTheme.spacing.xs
            }}>
                {statusOption.label}
            </span>
        );
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);

        const day = date.getDate();
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const year = date.getFullYear();

        let hours = date.getHours();
        const minutes = date.getMinutes().toString().padStart(2, '0');
        const ampm = hours >= 12 ? 'pm' : 'am';
        hours = hours % 12;
        hours = hours ? hours : 12;

        return `${day} ${month} ${year} ${hours}:${minutes} ${ampm}`;
    };

    // ================= UI Components =================

    const LoadingSpinner = () => (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: rexTechTheme.spacing.xxl,
            color: rexTechTheme.colors.primary.main
        }}>
            <div style={{
                width: '40px',
                height: '40px',
                border: `3px solid ${rexTechTheme.colors.primary.lightest}`,
                borderTop: `3px solid ${rexTechTheme.colors.primary.main}`,
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginBottom: rexTechTheme.spacing.md
            }} />
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
            <span style={{ color: rexTechTheme.colors.neutral.darkGray }}>Loading all requests...</span>
        </div>
    );

    const EmptyState = () => (
        <div style={{
            textAlign: 'center',
            padding: rexTechTheme.spacing.xxl,
            color: rexTechTheme.colors.neutral.darkGray
        }}>
            <Package size={48} style={{
                marginBottom: rexTechTheme.spacing.md,
                color: rexTechTheme.colors.neutral.gray,
                opacity: 0.5
            }} />
            <h3 style={{
                color: rexTechTheme.colors.neutral.darkGray,
                marginBottom: rexTechTheme.spacing.sm
            }}>
                No Requests Found
            </h3>
            <p style={{ marginBottom: rexTechTheme.spacing.lg }}>
                Get started by creating a new pickup request
            </p>
            <button
                onClick={() => { resetForm(); setShowModal(true); }}
                style={{
                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                    background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                    color: rexTechTheme.colors.neutral.white,
                    border: 'none',
                    borderRadius: rexTechTheme.borderRadius.medium,
                    fontSize: rexTechTheme.typography.fontSize.regular,
                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: rexTechTheme.spacing.sm,
                    margin: '0 auto',
                    boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                    ':hover': {
                        background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                    }
                }}
            >
                <Plus size={18} />
                Create New Request
            </button>
        </div>
    );

    return (
        <div style={{
            minHeight: '100vh',
            backgroundColor: rexTechTheme.colors.neutral.lightGray,
            fontFamily: rexTechTheme.typography.fontFamily
        }}>
            <AdminHeader />

            <div style={{
                maxWidth: '1600px',
                margin: '0 auto',
                padding: rexTechTheme.spacing.xl
            }}>
                {/* Header Section */}
                <div style={{
                    marginBottom: rexTechTheme.spacing.xl
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: rexTechTheme.spacing.lg
                    }}>
                        <div>
                            <h1 style={{
                                fontSize: rexTechTheme.typography.fontSize.xxlarge,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.bold,
                                marginBottom: rexTechTheme.spacing.xs
                            }}>
                                All Pickup Requests
                            </h1>
                            <p style={{
                                color: rexTechTheme.colors.neutral.darkGray,
                                fontSize: rexTechTheme.typography.fontSize.regular
                            }}>
                                Manage and track all pickup requests from all users
                            </p>
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: rexTechTheme.spacing.md,
                            alignItems: 'center'
                        }}>
                            {dashboardStats && (
                                <div style={{
                                    display: 'flex',
                                    gap: rexTechTheme.spacing.md,
                                    alignItems: 'center'
                                }}>
                                    <div style={{
                                        backgroundColor: rexTechTheme.colors.neutral.white,
                                        padding: `${rexTechTheme.spacing.sm} ${rexTechTheme.spacing.md}`,
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: rexTechTheme.spacing.sm
                                    }}>
                                        <div style={{
                                            color: rexTechTheme.colors.primary.main,
                                            fontWeight: rexTechTheme.typography.fontWeight.semiBold
                                        }}>
                                            Total: {dashboardStats.totalRequests || 0}
                                        </div>
                                        <div style={{
                                            height: '20px',
                                            width: '1px',
                                            backgroundColor: rexTechTheme.colors.neutral.gray
                                        }}></div>
                                        <div style={{
                                            color: rexTechTheme.colors.accent.blue,
                                            fontWeight: rexTechTheme.typography.fontWeight.semiBold
                                        }}>
                                            New: {dashboardStats.newRequests || 0}
                                        </div>
                                        <div style={{
                                            height: '20px',
                                            width: '1px',
                                            backgroundColor: rexTechTheme.colors.neutral.gray
                                        }}></div>
                                        <div style={{
                                            color: rexTechTheme.colors.accent.teal,
                                            fontWeight: rexTechTheme.typography.fontWeight.semiBold
                                        }}>
                                            Collected: {dashboardStats.collectedRequests || 0}
                                        </div>
                                    </div>
                                </div>
                            )}

                            <button
                                onClick={() => GetData()}
                                style={{
                                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.lg}`,
                                    backgroundColor: rexTechTheme.colors.neutral.white,
                                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                    color: rexTechTheme.colors.neutral.darkGray,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    fontWeight: rexTechTheme.typography.fontWeight.medium,
                                    cursor: 'pointer',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: rexTechTheme.spacing.sm,
                                    ':hover': {
                                        backgroundColor: rexTechTheme.colors.neutral.lightGray
                                    }
                                }}
                            >
                                <RefreshCw size={18} />
                                Refresh
                            </button>
                            {/* <button
                                onClick={() => { resetForm(); setShowModal(true); }}
                                style={{
                                    padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                    background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                                    color: rexTechTheme.colors.neutral.white,
                                    border: 'none',
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: rexTechTheme.spacing.sm,
                                    boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                                    ':hover': {
                                        background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                                    }
                                }}
                            >
                                <Plus size={18} />
                                New Request
                            </button> */}
                        </div>
                    </div>

                    {/* Filter Section */}
                    <div style={{
                        backgroundColor: rexTechTheme.colors.neutral.white,
                        borderRadius: rexTechTheme.borderRadius.large,
                        padding: rexTechTheme.spacing.lg,
                        boxShadow: rexTechTheme.shadows.light,
                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                        marginBottom: rexTechTheme.spacing.lg
                    }}>
                        <div style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr auto auto auto',
                            gap: rexTechTheme.spacing.md,
                            alignItems: 'center'
                        }}>
                            {/* Search */}
                            <div style={{ position: 'relative' }}>
                                <Search
                                    size={20}
                                    style={{
                                        position: 'absolute',
                                        left: rexTechTheme.spacing.md,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        color: rexTechTheme.colors.neutral.darkGray
                                    }}
                                />
                                <input
                                    type="text"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    placeholder="Search requests by  client, collector...."
                                    style={{
                                        width: '100%',
                                        padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                        paddingLeft: `calc(${rexTechTheme.spacing.xl} + 20px)`,
                                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        transition: 'all 0.2s ease',
                                        outline: 'none',
                                        boxSizing: 'border-box',
                                        ':focus': {
                                            borderColor: rexTechTheme.colors.primary.main,
                                            boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                        }
                                    }}
                                />
                            </div>

                            {/* Status Filter */}
                            <div>
                                <select
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    style={{
                                        padding: rexTechTheme.spacing.md,
                                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        backgroundColor: rexTechTheme.colors.neutral.white,
                                        cursor: 'pointer',
                                        minWidth: '150px',
                                        outline: 'none',
                                        ':focus': {
                                            borderColor: rexTechTheme.colors.primary.main
                                        }
                                    }}
                                >
                                    <option value="All">All Status</option>
                                    {statusOptions.map(option => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Date Range */}
                            <div style={{
                                display: 'flex',
                                gap: rexTechTheme.spacing.sm,
                                alignItems: 'center'
                            }}>
                                <input
                                    type="date"
                                    value={dateRange.from}
                                    onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
                                    style={{
                                        padding: rexTechTheme.spacing.md,
                                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        outline: 'none',
                                        ':focus': {
                                            borderColor: rexTechTheme.colors.primary.main
                                        }
                                    }}
                                />
                                <span style={{ color: rexTechTheme.colors.neutral.darkGray }}>to</span>
                                <input
                                    type="date"
                                    value={dateRange.to}
                                    onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
                                    style={{
                                        padding: rexTechTheme.spacing.md,
                                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        outline: 'none',
                                        ':focus': {
                                            borderColor: rexTechTheme.colors.primary.main
                                        }
                                    }}
                                />
                            </div>

                            {/* Results Count */}
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.sm,
                                color: rexTechTheme.colors.neutral.darkGray,
                                fontSize: rexTechTheme.typography.fontSize.small,
                                whiteSpace: 'nowrap'
                            }}>
                                <Filter size={16} />
                                <span>{filteredRequests.length} requests found</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Main Content */}
                <div style={{
                    backgroundColor: rexTechTheme.colors.neutral.white,
                    borderRadius: rexTechTheme.borderRadius.large,
                    boxShadow: rexTechTheme.shadows.light,
                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    overflow: 'hidden'
                }}>
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : filteredRequests.length === 0 ? (
                        <EmptyState />
                    ) : (
                        <>
                            <div style={{ overflowX: 'auto' }}>
                                <table style={{
                                    width: '100%',
                                    borderCollapse: 'collapse',
                                    minWidth: '1200px'
                                }}>
                                    <thead>
                                        <tr style={{
                                            backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                            borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`
                                        }}>
                                            <th style={{
                                                padding: rexTechTheme.spacing.lg,
                                                textAlign: 'left',
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontSize: rexTechTheme.typography.fontSize.regular,
                                                width: '250px'
                                            }}>
                                                Client
                                            </th>
                                            <th style={{
                                                padding: rexTechTheme.spacing.lg,
                                                textAlign: 'left',
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontSize: rexTechTheme.typography.fontSize.regular,
                                                width: '300px'
                                            }}>
                                                Pickup Details
                                            </th>
                                            <th style={{
                                                padding: rexTechTheme.spacing.lg,
                                                textAlign: 'left',
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontSize: rexTechTheme.typography.fontSize.regular,
                                                width: '150px'
                                            }}>
                                                Collector
                                            </th>
                                            <th style={{
                                                padding: rexTechTheme.spacing.lg,
                                                textAlign: 'left',
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontSize: rexTechTheme.typography.fontSize.regular,
                                                width: '120px'
                                            }}>
                                                Status
                                            </th>
                                            <th style={{
                                                padding: rexTechTheme.spacing.lg,
                                                textAlign: 'center',
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                color: rexTechTheme.colors.primary.dark,
                                                fontSize: rexTechTheme.typography.fontSize.regular,
                                                width: '250px'
                                            }}>
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentRequests.map((request, index) => (
                                            <React.Fragment key={request.requestId}>
                                                <tr
                                                    style={{
                                                        backgroundColor: index % 2 === 0
                                                            ? rexTechTheme.colors.neutral.white
                                                            : rexTechTheme.colors.neutral.lightGray,
                                                        borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                                        transition: 'all 0.2s ease',
                                                        cursor: 'pointer',
                                                        ':hover': {
                                                            backgroundColor: rexTechTheme.colors.primary.lightest
                                                        }
                                                    }}
                                                    onClick={() => setExpandedRequestId(
                                                        expandedRequestId === request.requestId ? null : request.requestId
                                                    )}
                                                >
                                                    <td style={{ padding: rexTechTheme.spacing.lg }}>
                                                        <div style={{ marginBottom: rexTechTheme.spacing.sm }}>
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: rexTechTheme.spacing.xs,
                                                                marginBottom: rexTechTheme.spacing.xs
                                                            }}>
                                                                <User size={12} color={rexTechTheme.colors.neutral.darkGray} />
                                                                <span style={{
                                                                    color: rexTechTheme.colors.neutral.darkGray,
                                                                    fontSize: rexTechTheme.typography.fontSize.small
                                                                }}>
                                                                    {request.userName || 'Unknown User'}
                                                                </span>
                                                            </div>
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: rexTechTheme.spacing.xs,
                                                                marginBottom: rexTechTheme.spacing.xs
                                                            }}>
                                                                <Mail size={12} color={rexTechTheme.colors.neutral.darkGray} />
                                                                <span style={{
                                                                    color: rexTechTheme.colors.neutral.darkGray,
                                                                    fontSize: rexTechTheme.typography.fontSize.small
                                                                }}>
                                                                    {request.userEmail || 'No email'}
                                                                </span>
                                                            </div>
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: rexTechTheme.spacing.xs
                                                            }}>
                                                                <Phone size={12} color={rexTechTheme.colors.neutral.darkGray} />
                                                                <span style={{
                                                                    color: rexTechTheme.colors.neutral.darkGray,
                                                                    fontSize: rexTechTheme.typography.fontSize.small
                                                                }}>
                                                                    {request.userPhone || 'No phone'}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: rexTechTheme.spacing.lg }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: rexTechTheme.spacing.xs,
                                                            marginBottom: rexTechTheme.spacing.xs
                                                        }}>
                                                            <Calendar size={14} color={rexTechTheme.colors.neutral.darkGray} />
                                                            <span style={{ color: rexTechTheme.colors.neutral.darkGray }}>
                                                                {formatDate(request.pickupDate)}
                                                            </span>
                                                        </div>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: rexTechTheme.spacing.xs
                                                        }}>
                                                            <Home size={12} color={rexTechTheme.colors.neutral.darkGray} />
                                                            <span style={{
                                                                color: rexTechTheme.colors.neutral.darkGray,
                                                                fontSize: rexTechTheme.typography.fontSize.small
                                                            }}>
                                                                {request.pickupAddress}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: rexTechTheme.spacing.lg }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: rexTechTheme.spacing.xs
                                                        }}>
                                                            <Truck size={12} color={rexTechTheme.colors.neutral.darkGray} />
                                                            <span style={{
                                                                color: rexTechTheme.colors.neutral.darkGray,
                                                                fontSize: rexTechTheme.typography.fontSize.small
                                                            }}>
                                                                {request.collectorName || 'Not assigned'}
                                                            </span>
                                                        </div>
                                                    </td>
                                                    <td style={{ padding: rexTechTheme.spacing.lg }}>
                                                        {getStatusBadge(request.status)}
                                                    </td>
                                                    <td style={{ padding: rexTechTheme.spacing.lg, textAlign: 'center' }}>
                                                        <div style={{
                                                            display: 'flex',
                                                            gap: rexTechTheme.spacing.xs,
                                                            justifyContent: 'center',
                                                            flexWrap: 'wrap'
                                                        }}>
                                                            {/* View Button */}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleView(request);
                                                                }}
                                                                style={{
                                                                    padding: rexTechTheme.spacing.sm,
                                                                    backgroundColor: rexTechTheme.colors.primary.lightest,
                                                                    border: `1px solid ${rexTechTheme.colors.primary.light}`,
                                                                    color: rexTechTheme.colors.primary.main,
                                                                    borderRadius: rexTechTheme.borderRadius.small,
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    minWidth: '40px',
                                                                    ':hover': {
                                                                        backgroundColor: rexTechTheme.colors.primary.light,
                                                                        color: rexTechTheme.colors.neutral.white
                                                                    }
                                                                }}
                                                                title="View Details"
                                                            >
                                                                <Eye size={16} />
                                                            </button>

                                                            {/* Edit Button */}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleEdit(request);
                                                                }}
                                                                style={{
                                                                    padding: rexTechTheme.spacing.sm,
                                                                    backgroundColor: rexTechTheme.colors.primary.lightest,
                                                                    border: `1px solid ${rexTechTheme.colors.primary.light}`,
                                                                    color: rexTechTheme.colors.primary.main,
                                                                    borderRadius: rexTechTheme.borderRadius.small,
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    minWidth: '40px',
                                                                    ':hover': {
                                                                        backgroundColor: rexTechTheme.colors.primary.light,
                                                                        color: rexTechTheme.colors.neutral.white
                                                                    }
                                                                }}
                                                                title="Edit Request"
                                                            >
                                                                <Edit2 size={16} />
                                                            </button>

                                                            {/* Status Update Button */}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleStatusButtonClick(request);
                                                                }}
                                                                disabled={request.status === 'RECYCLED'}
                                                                style={{
                                                                    padding: rexTechTheme.spacing.sm,
                                                                    backgroundColor: request.status === 'RECYCLED'
                                                                        ? rexTechTheme.colors.neutral.lightGray
                                                                        : rexTechTheme.colors.accent.teal + '20',
                                                                    border: `1px solid ${request.status === 'RECYCLED'
                                                                        ? rexTechTheme.colors.neutral.gray
                                                                        : rexTechTheme.colors.accent.teal}`,
                                                                    color: request.status === 'RECYCLED'
                                                                        ? rexTechTheme.colors.neutral.gray
                                                                        : rexTechTheme.colors.accent.teal,
                                                                    borderRadius: rexTechTheme.borderRadius.small,
                                                                    cursor: request.status === 'RECYCLED' ? 'not-allowed' : 'pointer',
                                                                    transition: 'all 0.2s ease',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    minWidth: '40px',
                                                                    ':hover': request.status === 'RECYCLED' ? {} : {
                                                                        backgroundColor: rexTechTheme.colors.accent.teal,
                                                                        color: rexTechTheme.colors.neutral.white
                                                                    }
                                                                }}
                                                                title={request.status === 'RECYCLED' ? 'Request Completed' : 'Update to Next Status'}
                                                            >
                                                                <RefreshCw size={16} />
                                                            </button>

                                                            {/* Assign Collector Button */}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedRequest(request);
                                                                    setShowAssignModal(true);
                                                                }}
                                                                style={{
                                                                    padding: rexTechTheme.spacing.sm,
                                                                    backgroundColor: rexTechTheme.colors.accent.blue + '20',
                                                                    border: `1px solid ${rexTechTheme.colors.accent.blue}`,
                                                                    color: rexTechTheme.colors.accent.blue,
                                                                    borderRadius: rexTechTheme.borderRadius.small,
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    minWidth: '40px',
                                                                    ':hover': {
                                                                        backgroundColor: rexTechTheme.colors.accent.blue,
                                                                        color: rexTechTheme.colors.neutral.white
                                                                    }
                                                                }}
                                                                title="Assign/Change Collector"
                                                            >
                                                                <Users size={16} />
                                                            </button>

                                                            {/* Delete Button */}
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setRequestToDelete(request.requestId);
                                                                    setShowDeleteModal(true);
                                                                }}
                                                                style={{
                                                                    padding: rexTechTheme.spacing.sm,
                                                                    backgroundColor: rexTechTheme.colors.accent.orange + '20',
                                                                    border: `1px solid ${rexTechTheme.colors.accent.orange}`,
                                                                    color: rexTechTheme.colors.accent.orange,
                                                                    borderRadius: rexTechTheme.borderRadius.small,
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.2s ease',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    minWidth: '40px',
                                                                    ':hover': {
                                                                        backgroundColor: rexTechTheme.colors.accent.orange,
                                                                        color: rexTechTheme.colors.neutral.white
                                                                    }
                                                                }}
                                                                title="Delete Request"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>

                                                {/* Expanded Row with Items */}
                                                {expandedRequestId === request.requestId && request.requestItems && (
                                                    <tr>
                                                        <td colSpan="5" style={{
                                                            backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                                            padding: rexTechTheme.spacing.lg,
                                                            borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`
                                                        }}>
                                                            <h4 style={{
                                                                marginBottom: rexTechTheme.spacing.md,
                                                                color: rexTechTheme.colors.primary.dark
                                                            }}>
                                                                <Package size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                                                Pickup Items ({request.requestItems.length})
                                                            </h4>
                                                            <div style={{
                                                                display: 'grid',
                                                                gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                                                gap: rexTechTheme.spacing.md
                                                            }}>
                                                                {request.requestItems.map((item, idx) => (
                                                                    <div key={idx} style={{
                                                                        backgroundColor: rexTechTheme.colors.neutral.white,
                                                                        padding: rexTechTheme.spacing.md,
                                                                        borderRadius: rexTechTheme.borderRadius.medium,
                                                                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center'
                                                                    }}>
                                                                        <div>
                                                                            <div style={{
                                                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                                                marginBottom: rexTechTheme.spacing.xs
                                                                            }}>
                                                                                {item.itemName || getPickupItemName(item.pickupItemId)}
                                                                            </div>
                                                                            <div style={{
                                                                                display: 'flex',
                                                                                gap: rexTechTheme.spacing.lg,
                                                                                fontSize: rexTechTheme.typography.fontSize.small,
                                                                                color: rexTechTheme.colors.neutral.darkGray
                                                                            }}>
                                                                                {item.pallet && (
                                                                                    <span>
                                                                                        <Building size={12} style={{ marginRight: '4px' }} />
                                                                                        {item.pallet} pallets
                                                                                    </span>
                                                                                )}
                                                                                {item.weight && (
                                                                                    <span>
                                                                                        <BarChart size={12} style={{ marginRight: '4px' }} />
                                                                                        {item.weight} kgs,lbs
                                                                                    </span>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            {/* Pagination */}
                            {totalPages > 1 && (
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: rexTechTheme.spacing.lg,
                                    borderTop: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                    backgroundColor: rexTechTheme.colors.neutral.lightGray
                                }}>
                                    <div style={{
                                        color: rexTechTheme.colors.neutral.darkGray,
                                        fontSize: rexTechTheme.typography.fontSize.small
                                    }}>
                                        Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredRequests.length)} of {filteredRequests.length} requests
                                    </div>
                                    <div style={{
                                        display: 'flex',
                                        gap: rexTechTheme.spacing.xs,
                                        alignItems: 'center'
                                    }}>
                                        <button
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                                            disabled={currentPage === 1}
                                            style={{
                                                padding: `${rexTechTheme.spacing.sm} ${rexTechTheme.spacing.md}`,
                                                backgroundColor: currentPage === 1 ? rexTechTheme.colors.neutral.lightGray : rexTechTheme.colors.neutral.white,
                                                border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                                color: currentPage === 1 ? rexTechTheme.colors.neutral.gray : rexTechTheme.colors.neutral.darkGray,
                                                borderRadius: rexTechTheme.borderRadius.small,
                                                cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                                                fontSize: rexTechTheme.typography.fontSize.small,
                                                ':hover': currentPage === 1 ? {} : {
                                                    backgroundColor: rexTechTheme.colors.neutral.lightGray
                                                }
                                            }}
                                        >
                                            Previous
                                        </button>

                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNum;
                                            if (totalPages <= 5) {
                                                pageNum = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNum = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNum = totalPages - 4 + i;
                                            } else {
                                                pageNum = currentPage - 2 + i;
                                            }

                                            return (
                                                <button
                                                    key={pageNum}
                                                    onClick={() => setCurrentPage(pageNum)}
                                                    style={{
                                                        padding: `${rexTechTheme.spacing.sm} ${rexTechTheme.spacing.md}`,
                                                        backgroundColor: currentPage === pageNum ? rexTechTheme.colors.primary.main : rexTechTheme.colors.neutral.white,
                                                        border: `1px solid ${currentPage === pageNum ? rexTechTheme.colors.primary.main : rexTechTheme.colors.neutral.gray}`,
                                                        color: currentPage === pageNum ? rexTechTheme.colors.neutral.white : rexTechTheme.colors.neutral.darkGray,
                                                        borderRadius: rexTechTheme.borderRadius.small,
                                                        cursor: 'pointer',
                                                        fontSize: rexTechTheme.typography.fontSize.small,
                                                        minWidth: '40px',
                                                        ':hover': currentPage === pageNum ? {} : {
                                                            backgroundColor: rexTechTheme.colors.neutral.lightGray
                                                        }
                                                    }}
                                                >
                                                    {pageNum}
                                                </button>
                                            );
                                        })}

                                        <button
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                                            disabled={currentPage === totalPages}
                                            style={{
                                                padding: `${rexTechTheme.spacing.sm} ${rexTechTheme.spacing.md}`,
                                                backgroundColor: currentPage === totalPages ? rexTechTheme.colors.neutral.lightGray : rexTechTheme.colors.neutral.white,
                                                border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                                color: currentPage === totalPages ? rexTechTheme.colors.neutral.gray : rexTechTheme.colors.neutral.darkGray,
                                                borderRadius: rexTechTheme.borderRadius.small,
                                                cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                                                fontSize: rexTechTheme.typography.fontSize.small,
                                                ':hover': currentPage === totalPages ? {} : {
                                                    backgroundColor: rexTechTheme.colors.neutral.lightGray
                                                }
                                            }}
                                        >
                                            Next
                                        </button>
                                    </div>

                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: rexTechTheme.spacing.sm,
                                        color: rexTechTheme.colors.neutral.darkGray,
                                        fontSize: rexTechTheme.typography.fontSize.small
                                    }}>
                                        <span>Items per page:</span>
                                        <select
                                            value={itemsPerPage}
                                            onChange={(e) => {
                                                setItemsPerPage(Number(e.target.value));
                                                setCurrentPage(1);
                                            }}
                                            style={{
                                                padding: `${rexTechTheme.spacing.xs} ${rexTechTheme.spacing.sm}`,
                                                border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                                borderRadius: rexTechTheme.borderRadius.small,
                                                fontSize: rexTechTheme.typography.fontSize.small,
                                                backgroundColor: rexTechTheme.colors.neutral.white
                                            }}
                                        >
                                            <option value="5">5</option>
                                            <option value="10">10</option>
                                            <option value="20">20</option>
                                            <option value="50">50</option>
                                        </select>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Add/Edit Request Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} centered size="lg">
                <Modal.Header closeButton style={{
                    borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    backgroundColor: rexTechTheme.colors.neutral.lightGray
                }}>
                    <Modal.Title style={{
                        color: rexTechTheme.colors.primary.dark,
                        fontWeight: rexTechTheme.typography.fontWeight.bold
                    }}>
                        {values.requestId === '00000000-0000-0000-0000-000000000000' ? (
                            <>
                                <Plus size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                New Pickup Request
                            </>
                        ) : (
                            <>
                                <Edit2 size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                Edit Request Details
                            </>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: rexTechTheme.spacing.xl }}>
                    <form onSubmit={handleSubmit}>
                       
                       

                        {/* Pickup Items Section */}
                        <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: rexTechTheme.spacing.md
                            }}>
                                <label style={{
                                    color: rexTechTheme.colors.primary.dark,
                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                    fontSize: rexTechTheme.typography.fontSize.regular
                                }}>
                                    <Package size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                    Pickup Items
                                    <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '4px' }}>*</span>
                                </label>
                                <div style={{
                                    color: rexTechTheme.colors.neutral.darkGray,
                                    fontSize: rexTechTheme.typography.fontSize.small
                                }}>
                                    {values.requestItems.length} item(s) added
                                </div>
                            </div>

                            {errors.requestItems && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: rexTechTheme.spacing.xs,
                                    marginBottom: rexTechTheme.spacing.md,
                                    color: rexTechTheme.colors.accent.orange,
                                    fontSize: rexTechTheme.typography.fontSize.small
                                }}>
                                    <AlertCircle size={14} />
                                    <span>{errors.requestItems}</span>
                                </div>
                            )}

                            {/* Add Item Form */}
                            <div style={{
                                backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                padding: rexTechTheme.spacing.lg,
                                borderRadius: rexTechTheme.borderRadius.medium,
                                marginBottom: rexTechTheme.spacing.lg
                            }}>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '2fr 1fr 1fr auto',
                                    gap: rexTechTheme.spacing.md,
                                    alignItems: 'end'
                                }}>
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: rexTechTheme.spacing.xs,
                                            fontSize: rexTechTheme.typography.fontSize.small,
                                            color: rexTechTheme.colors.neutral.darkGray
                                        }}>
                                            Item
                                        </label>
                                        <select
                                            name="pickupItemId"
                                            value={itemValues.pickupItemId}
                                            onChange={handleItemInput}
                                            style={{
                                                width: '100%',
                                                padding: rexTechTheme.spacing.md,
                                                border: `2px solid ${itemErrors.pickupItemId ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                                borderRadius: rexTechTheme.borderRadius.medium,
                                                fontSize: rexTechTheme.typography.fontSize.regular,
                                                backgroundColor: rexTechTheme.colors.neutral.white,
                                                outline: 'none',
                                                ':focus': {
                                                    borderColor: rexTechTheme.colors.primary.main
                                                }
                                            }}
                                        >
                                            <option value="">Select item</option>
                                            {pickupItems.map(item => (
                                                <option key={item.pickupItemId} value={item.pickupItemId}>
                                                    {item.pickupItemName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: rexTechTheme.spacing.xs,
                                            fontSize: rexTechTheme.typography.fontSize.small,
                                            color: rexTechTheme.colors.neutral.darkGray
                                        }}>
                                            Pallets
                                        </label>
                                        <input
                                            type="number"
                                            name="pallet"
                                            value={itemValues.pallet}
                                            onChange={handleItemInput}
                                            min="0"
                                            max="1000"
                                            step="0.01"
                                            style={{
                                                width: '100%',
                                                padding: rexTechTheme.spacing.md,
                                                border: `2px solid ${itemErrors.pallet ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                                borderRadius: rexTechTheme.borderRadius.medium,
                                                fontSize: rexTechTheme.typography.fontSize.regular,
                                                outline: 'none',
                                                ':focus': {
                                                    borderColor: rexTechTheme.colors.primary.main
                                                }
                                            }}
                                            placeholder="0.00"
                                        />
                                    </div>
                                    <div>
                                        <label style={{
                                            display: 'block',
                                            marginBottom: rexTechTheme.spacing.xs,
                                            fontSize: rexTechTheme.typography.fontSize.small,
                                            color: rexTechTheme.colors.neutral.darkGray
                                        }}>
                                            Weight
                                        </label>
                                        <input
                                            type="test"
                                            name="weight"
                                            value={itemValues.weight}
                                            onChange={handleItemInput}
                                            style={{
                                                width: '100%',
                                                padding: rexTechTheme.spacing.md,
                                                border: `2px solid ${itemErrors.weight ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                                borderRadius: rexTechTheme.borderRadius.medium,
                                                fontSize: rexTechTheme.typography.fontSize.regular,
                                                outline: 'none',
                                                ':focus': {
                                                    borderColor: rexTechTheme.colors.primary.main
                                                }
                                            }}
                                            placeholder="kgs,lbs"
                                        />
                                    </div>
                                    <button
                                        type="button"
                                        onClick={addRequestItem}
                                        style={{
                                            padding: rexTechTheme.spacing.md,
                                            backgroundColor: rexTechTheme.colors.primary.main,
                                            color: rexTechTheme.colors.neutral.white,
                                            border: 'none',
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            ':hover': {
                                                backgroundColor: rexTechTheme.colors.primary.dark
                                            }
                                        }}
                                    >
                                        {itemValues.requestItemId === '00000000-0000-0000-0000-000000000000' ? 'Add' : 'Update'}
                                    </button>
                                </div>
                            </div>

                            {/* Items List */}
                            {values.requestItems.length > 0 && (
                                <div style={{
                                    maxHeight: '200px',
                                    overflowY: 'auto',
                                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                    borderRadius: rexTechTheme.borderRadius.medium
                                }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead style={{
                                            backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                            position: 'sticky',
                                            top: 0
                                        }}>
                                            <tr>
                                                <th style={{ padding: rexTechTheme.spacing.md, textAlign: 'left' }}>Item</th>
                                                <th style={{ padding: rexTechTheme.spacing.md, textAlign: 'center' }}>Pallets</th>
                                                <th style={{ padding: rexTechTheme.spacing.md, textAlign: 'center' }}>Weight</th>
                                                <th style={{ padding: rexTechTheme.spacing.md, textAlign: 'center' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {values.requestItems.map((item, index) => (
                                                <tr key={index} style={{
                                                    borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                                    ':last-child': { borderBottom: 'none' }
                                                }}>
                                                    <td style={{ padding: rexTechTheme.spacing.md }}>
                                                        {getPickupItemName(item.pickupItemId)}
                                                    </td>
                                                    <td style={{ padding: rexTechTheme.spacing.md, textAlign: 'center' }}>
                                                        {item.pallet || '-'}
                                                    </td>
                                                    <td style={{ padding: rexTechTheme.spacing.md, textAlign: 'center' }}>
                                                        {item.weight ? `${item.weight} kg` : '-'}
                                                    </td>
                                                    <td style={{ padding: rexTechTheme.spacing.md, textAlign: 'center' }}>
                                                        <div style={{ display: 'flex', gap: rexTechTheme.spacing.xs, justifyContent: 'center' }}>
                                                            <button
                                                                type="button"
                                                                onClick={() => editRequestItem(item)}
                                                                style={{
                                                                    padding: rexTechTheme.spacing.xs,
                                                                    backgroundColor: rexTechTheme.colors.primary.lightest,
                                                                    border: `1px solid ${rexTechTheme.colors.primary.light}`,
                                                                    color: rexTechTheme.colors.primary.main,
                                                                    borderRadius: rexTechTheme.borderRadius.small,
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                <Edit2 size={14} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeRequestItem(index)}
                                                                style={{
                                                                    padding: rexTechTheme.spacing.xs,
                                                                    backgroundColor: rexTechTheme.colors.primary.lightest,
                                                                    border: `1px solid ${rexTechTheme.colors.accent.orange}`,
                                                                    color: rexTechTheme.colors.accent.orange,
                                                                    borderRadius: rexTechTheme.borderRadius.small,
                                                                    cursor: 'pointer'
                                                                }}
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Pickup Date & Time */}
                        <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
                            <label style={{
                                display: 'block',
                                marginBottom: rexTechTheme.spacing.xs,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold
                            }}>
                                <Calendar size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                Pickup Date & Time
                                <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '4px' }}>*</span>
                            </label>
                            <input
                                type="datetime-local"
                                name="pickupDate"
                                value={values.pickupDate}
                                onChange={handleInput}
                                style={{
                                    width: '100%',
                                    padding: rexTechTheme.spacing.md,
                                    border: `2px solid ${errors.pickupDate ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    ':focus': {
                                        borderColor: rexTechTheme.colors.primary.main,
                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                    }
                                }}
                            />
                            {errors.pickupDate && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: rexTechTheme.spacing.xs,
                                    marginTop: rexTechTheme.spacing.xs,
                                    color: rexTechTheme.colors.accent.orange,
                                    fontSize: rexTechTheme.typography.fontSize.small
                                }}>
                                    <AlertCircle size={14} />
                                    <span>{errors.pickupDate}</span>
                                </div>
                            )}
                        </div>

                        {/* Pickup Address */}
                        <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
                            <label style={{
                                display: 'block',
                                marginBottom: rexTechTheme.spacing.xs,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold
                            }}>
                                <MapPin size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                Pickup Address
                                <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '4px' }}>*</span>
                            </label>
                            <textarea
                                name="pickupAddress"
                                value={values.pickupAddress}
                                onChange={handleInput}
                                rows="3"
                                style={{
                                    width: '100%',
                                    padding: rexTechTheme.spacing.md,
                                    border: `2px solid ${errors.pickupAddress ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    resize: 'vertical',
                                    ':focus': {
                                        borderColor: rexTechTheme.colors.primary.main,
                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                    }
                                }}
                                placeholder="Enter complete pickup address..."
                                maxLength="500"
                            />
                            {errors.pickupAddress && (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: rexTechTheme.spacing.xs,
                                    marginTop: rexTechTheme.spacing.xs,
                                    color: rexTechTheme.colors.accent.orange,
                                    fontSize: rexTechTheme.typography.fontSize.small
                                }}>
                                    <AlertCircle size={14} />
                                    <span>{errors.pickupAddress}</span>
                                </div>
                            )}
                            <div style={{
                                marginTop: rexTechTheme.spacing.xs,
                                fontSize: rexTechTheme.typography.fontSize.xs,
                                color: rexTechTheme.colors.neutral.darkGray,
                                textAlign: 'right'
                            }}>
                                {values.pickupAddress.length}/500 characters
                            </div>
                        </div>

                        {/* Notes */}
                        <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
                            <label style={{
                                display: 'block',
                                marginBottom: rexTechTheme.spacing.xs,
                                color: rexTechTheme.colors.primary.dark,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold
                            }}>
                                <FileText size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                                Notes (Optional)
                            </label>
                            <textarea
                                name="notes"
                                value={values.notes}
                                onChange={handleInput}
                                rows="2"
                                style={{
                                    width: '100%',
                                    padding: rexTechTheme.spacing.md,
                                    border: `2px solid ${errors.notes ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    transition: 'all 0.2s ease',
                                    outline: 'none',
                                    boxSizing: 'border-box',
                                    resize: 'vertical',
                                    ':focus': {
                                        borderColor: rexTechTheme.colors.primary.main,
                                        boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                    }
                                }}
                                placeholder="Any special instructions or notes..."
                                maxLength="1000"
                            />
                            <div style={{
                                marginTop: rexTechTheme.spacing.xs,
                                fontSize: rexTechTheme.typography.fontSize.xs,
                                color: rexTechTheme.colors.neutral.darkGray,
                                textAlign: 'right'
                            }}>
                                {values.notes.length}/1000 characters
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div style={{
                            display: 'flex',
                            gap: rexTechTheme.spacing.md,
                            marginTop: rexTechTheme.spacing.xl
                        }}>
                            <button
                                type="button"
                                onClick={() => setShowModal(false)}
                                style={{
                                    flex: 1,
                                    padding: rexTechTheme.spacing.md,
                                    backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                    border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                    color: rexTechTheme.colors.neutral.darkGray,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    cursor: 'pointer',
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                    transition: 'all 0.2s ease',
                                    ':hover': {
                                        backgroundColor: rexTechTheme.colors.neutral.gray
                                    }
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={btnLoading}
                                style={{
                                    flex: 1,
                                    padding: rexTechTheme.spacing.md,
                                    background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                                    color: rexTechTheme.colors.neutral.white,
                                    border: 'none',
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    fontSize: rexTechTheme.typography.fontSize.regular,
                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                    cursor: btnLoading ? 'not-allowed' : 'pointer',
                                    transition: 'all 0.3s ease',
                                    opacity: btnLoading ? 0.7 : 1,
                                    boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                                    ':hover': btnLoading ? {} : {
                                        background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                                    }
                                }}
                            >
                                {btnLoading ? (
                                    <>
                                        <div style={{
                                            width: '16px',
                                            height: '16px',
                                            border: `2px solid ${rexTechTheme.colors.neutral.white}`,
                                            borderTopColor: 'transparent',
                                            borderRadius: '50%',
                                            animation: 'spin 1s linear infinite',
                                            display: 'inline-block',
                                            marginRight: '8px',
                                            verticalAlign: 'middle'
                                        }} />
                                        Saving...
                                    </>
                                ) : values.requestId === '00000000-0000-0000-0000-000000000000' ? (
                                    <>
                                        <CheckCircle size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                        Create Request
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                        Update Request
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </Modal.Body>
            </Modal>

            {/* View Request Modal */}
            <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="lg">
                <Modal.Header closeButton style={{
                    borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    backgroundColor: rexTechTheme.colors.neutral.lightGray
                }}>
                    <Modal.Title style={{
                        color: rexTechTheme.colors.primary.dark,
                        fontWeight: rexTechTheme.typography.fontWeight.bold,
                        display: 'flex',
                        alignItems: 'center',
                        gap: rexTechTheme.spacing.sm
                    }}>
                        <Eye size={20} />
                        Request Details
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: rexTechTheme.spacing.xl }}>
                    {selectedRequest && (
                        <div>
                            {/* Header with Status */}
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: rexTechTheme.spacing.lg,
                                paddingBottom: rexTechTheme.spacing.lg,
                                borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`
                            }}>
                                <div>
                                    <h4 style={{
                                        color: rexTechTheme.colors.primary.dark,
                                        marginBottom: rexTechTheme.spacing.xs
                                    }}>
                                        {selectedRequest.pickupAddress}
                                    </h4>
                                    <div style={{
                                        color: rexTechTheme.colors.neutral.darkGray,
                                        fontSize: rexTechTheme.typography.fontSize.small
                                    }}>
                                        Created: {formatDate(selectedRequest.createdDate)}
                                    </div>
                                </div>
                                {getStatusBadge(selectedRequest.status)}
                            </div>

                            {/* Two Column Layout */}
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: rexTechTheme.spacing.xl,
                                marginBottom: rexTechTheme.spacing.xl
                            }}>
                                {/* Left Column - User Info */}
                                <div>
                                    <h5 style={{
                                        color: rexTechTheme.colors.primary.dark,
                                        marginBottom: rexTechTheme.spacing.md,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: rexTechTheme.spacing.sm
                                    }}>
                                        <User size={16} />
                                        Client Information
                                    </h5>
                                    <div style={{
                                        backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                        padding: rexTechTheme.spacing.lg,
                                        borderRadius: rexTechTheme.borderRadius.medium
                                    }}>
                                        <div style={{ marginBottom: rexTechTheme.spacing.md }}>
                                            <div style={{
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                color: rexTechTheme.colors.primary.dark,
                                                marginBottom: rexTechTheme.spacing.xs
                                            }}>
                                                {selectedRequest.userName || 'N/A'}
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: rexTechTheme.spacing.sm,
                                                color: rexTechTheme.colors.neutral.darkGray,
                                                fontSize: rexTechTheme.typography.fontSize.small,
                                                marginBottom: rexTechTheme.spacing.xs
                                            }}>
                                                <Mail size={12} />
                                                {selectedRequest.userEmail || 'N/A'}
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: rexTechTheme.spacing.sm,
                                                color: rexTechTheme.colors.neutral.darkGray,
                                                fontSize: rexTechTheme.typography.fontSize.small
                                            }}>
                                                <Phone size={12} />
                                                {selectedRequest.userPhone || 'N/A'}
                                            </div>
                                        </div>
                                    </div>

                                    <h5 style={{
                                        color: rexTechTheme.colors.primary.dark,
                                        marginTop: rexTechTheme.spacing.lg,
                                        marginBottom: rexTechTheme.spacing.md,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: rexTechTheme.spacing.sm
                                    }}>
                                        <Calendar size={16} />
                                        Pickup Schedule
                                    </h5>
                                    <div style={{
                                        backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                        padding: rexTechTheme.spacing.lg,
                                        borderRadius: rexTechTheme.borderRadius.medium
                                    }}>
                                        <div style={{ marginBottom: rexTechTheme.spacing.md }}>
                                            <div style={{
                                                fontWeight: rexTechTheme.typography.fontWeight.medium,
                                                color: rexTechTheme.colors.neutral.darkGray,
                                                fontSize: rexTechTheme.typography.fontSize.small,
                                                marginBottom: rexTechTheme.spacing.xs
                                            }}>
                                                Date & Time
                                            </div>
                                            <div style={{
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                color: rexTechTheme.colors.primary.dark
                                            }}>
                                                {formatDate(selectedRequest.pickupDate)}
                                            </div>
                                        </div>
                                        <div>
                                            <div style={{
                                                fontWeight: rexTechTheme.typography.fontWeight.medium,
                                                color: rexTechTheme.colors.neutral.darkGray,
                                                fontSize: rexTechTheme.typography.fontSize.small,
                                                marginBottom: rexTechTheme.spacing.xs
                                            }}>
                                                Address
                                            </div>
                                            <div style={{
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                color: rexTechTheme.colors.primary.dark
                                            }}>
                                                {selectedRequest.pickupAddress || 'N/A'}
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Right Column - Collector & Notes */}
                                <div>
                                    <h5 style={{
                                        color: rexTechTheme.colors.primary.dark,
                                        marginBottom: rexTechTheme.spacing.md,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: rexTechTheme.spacing.sm
                                    }}>
                                        <Truck size={16} />
                                        Collector Information
                                    </h5>
                                    {selectedRequest.collectorName ? (
                                        <div style={{
                                            backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                            padding: rexTechTheme.spacing.lg,
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            marginBottom: rexTechTheme.spacing.lg
                                        }}>
                                            <div style={{ marginBottom: rexTechTheme.spacing.md }}>
                                                <div style={{
                                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                    color: rexTechTheme.colors.primary.dark,
                                                    marginBottom: rexTechTheme.spacing.xs
                                                }}>
                                                    {selectedRequest.collectorName}
                                                </div>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: rexTechTheme.spacing.sm,
                                                    color: rexTechTheme.colors.neutral.darkGray,
                                                    fontSize: rexTechTheme.typography.fontSize.small,
                                                    marginBottom: rexTechTheme.spacing.xs
                                                }}>
                                                    <Mail size={12} />
                                                    {selectedRequest.collectorEmail || 'N/A'}
                                                </div>
                                                <div style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: rexTechTheme.spacing.sm,
                                                    color: rexTechTheme.colors.neutral.darkGray,
                                                    fontSize: rexTechTheme.typography.fontSize.small
                                                }}>
                                                    <Phone size={12} />
                                                    {selectedRequest.collectorPhone || 'N/A'}
                                                </div>
                                            </div>
                                            {/* Display Truck and Seal Numbers */}
                                            {(selectedRequest.collectorDetails.truckReferenceNumber || selectedRequest.collectorDetails.sealNumber) && (
                                                <div style={{
                                                    borderTop: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                                    paddingTop: rexTechTheme.spacing.md,
                                                    marginTop: rexTechTheme.spacing.md
                                                }}>
                                                    <div style={{
                                                        display: 'grid',
                                                        gridTemplateColumns: '1fr 1fr',
                                                        gap: rexTechTheme.spacing.md,
                                                        fontSize: rexTechTheme.typography.fontSize.small
                                                    }}>
                                                        {selectedRequest.collectorDetails.truckReferenceNumber && (
                                                            <div>
                                                                <div style={{
                                                                    fontWeight: rexTechTheme.typography.fontWeight.medium,
                                                                    color: rexTechTheme.colors.neutral.darkGray,
                                                                    marginBottom: rexTechTheme.spacing.xs,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: rexTechTheme.spacing.xs
                                                                }}>
                                                                    <Key size={12} />
                                                                    Truck Ref No.
                                                                </div>
                                                                <div style={{
                                                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                                    color: rexTechTheme.colors.primary.dark
                                                                }}>
                                                                    {selectedRequest.collectorDetails.truckReferenceNumber}
                                                                </div>
                                                            </div>
                                                        )}
                                                        {selectedRequest.collectorDetails.sealNumber && (
                                                            <div>
                                                                <div style={{
                                                                    fontWeight: rexTechTheme.typography.fontWeight.medium,
                                                                    color: rexTechTheme.colors.neutral.darkGray,
                                                                    marginBottom: rexTechTheme.spacing.xs,
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: rexTechTheme.spacing.xs
                                                                }}>
                                                                    <Hash size={12} />
                                                                    Seal No.
                                                                </div>
                                                                <div style={{
                                                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                                    color: rexTechTheme.colors.primary.dark
                                                                }}>
                                                                    {selectedRequest.collectorDetails.sealNumber}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <div style={{
                                            backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                            padding: rexTechTheme.spacing.lg,
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            textAlign: 'center',
                                            color: rexTechTheme.colors.neutral.darkGray,
                                            marginBottom: rexTechTheme.spacing.lg
                                        }}>
                                            <Truck size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                                            <div>No collector assigned</div>
                                        </div>
                                    )}

                                    {selectedRequest.notes && (
                                        <>
                                            <h5 style={{
                                                color: rexTechTheme.colors.primary.dark,
                                                marginBottom: rexTechTheme.spacing.md,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: rexTechTheme.spacing.sm
                                            }}>
                                                <FileText size={16} />
                                                Notes
                                            </h5>
                                            <div style={{
                                                backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                                padding: rexTechTheme.spacing.lg,
                                                borderRadius: rexTechTheme.borderRadius.medium,
                                                whiteSpace: 'pre-wrap'
                                            }}>
                                                {selectedRequest.collectorDetails.notes}
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Items Section */}
                            <h5 style={{
                                color: rexTechTheme.colors.primary.dark,
                                marginBottom: rexTechTheme.spacing.md,
                                display: 'flex',
                                alignItems: 'center',
                                gap: rexTechTheme.spacing.sm
                            }}>
                                <Package size={16} />
                                Pickup Items ({selectedRequest.requestItems?.length || 0})
                            </h5>
                            {selectedRequest.requestItems && selectedRequest.requestItems.length > 0 ? (
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                                    gap: rexTechTheme.spacing.md
                                }}>
                                    {selectedRequest.requestItems.map((item, index) => (
                                        <div key={index} style={{
                                            backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                            padding: rexTechTheme.spacing.lg,
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            border: `1px solid ${rexTechTheme.colors.neutral.gray}`
                                        }}>
                                            <div style={{
                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                color: rexTechTheme.colors.primary.dark,
                                                marginBottom: rexTechTheme.spacing.md
                                            }}>
                                                {item.itemName || getPickupItemName(item.pickupItemId)}
                                            </div>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                fontSize: rexTechTheme.typography.fontSize.small,
                                                color: rexTechTheme.colors.neutral.darkGray
                                            }}>
                                                <div>
                                                    {item.pallet && (
                                                        <div style={{ marginBottom: rexTechTheme.spacing.xs }}>
                                                            <Building size={12} style={{ marginRight: '4px' }} />
                                                            {item.pallet} pallets
                                                        </div>
                                                    )}
                                                    {item.weight && (
                                                        <div>
                                                            <BarChart size={12} style={{ marginRight: '4px' }} />
                                                            {item.weight} kgs,lbs
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div style={{
                                    backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                    padding: rexTechTheme.spacing.xl,
                                    borderRadius: rexTechTheme.borderRadius.medium,
                                    textAlign: 'center',
                                    color: rexTechTheme.colors.neutral.darkGray
                                }}>
                                    <Package size={32} style={{ marginBottom: '16px', opacity: 0.5 }} />
                                    <div>No items in this request</div>
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button
                        onClick={() => setShowViewModal(false)}
                        style={{
                            padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                            backgroundColor: rexTechTheme.colors.neutral.lightGray,
                            border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                            color: rexTechTheme.colors.neutral.darkGray,
                            borderRadius: rexTechTheme.borderRadius.medium,
                            cursor: 'pointer',
                            fontSize: rexTechTheme.typography.fontSize.regular,
                            fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                            transition: 'all 0.2s ease',
                            ':hover': {
                                backgroundColor: rexTechTheme.colors.neutral.gray
                            }
                        }}
                    >
                        Close
                    </button>
                </Modal.Footer>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
                <Modal.Header closeButton style={{
                    borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    backgroundColor: rexTechTheme.colors.primary.lightest
                }}>
                    <Modal.Title style={{
                        color: rexTechTheme.colors.accent.orange,
                        fontWeight: rexTechTheme.typography.fontWeight.bold
                    }}>
                        <AlertCircle size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                        Confirm Deletion
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: rexTechTheme.spacing.xl }}>
                    <div style={{ textAlign: 'center', marginBottom: rexTechTheme.spacing.lg }}>
                        <XCircle size={48} style={{
                            color: rexTechTheme.colors.accent.orange,
                            marginBottom: rexTechTheme.spacing.md
                        }} />
                        <h4 style={{
                            color: rexTechTheme.colors.primary.dark,
                            marginBottom: rexTechTheme.spacing.sm
                        }}>
                            Delete Request?
                        </h4>
                        <p style={{
                            color: rexTechTheme.colors.neutral.darkGray,
                            lineHeight: 1.5
                        }}>
                            Are you sure you want to delete this request?
                            This action cannot be undone.
                        </p>
                    </div>
                    <div style={{
                        display: 'flex',
                        gap: rexTechTheme.spacing.md
                    }}>
                        <button
                            onClick={() => setShowDeleteModal(false)}
                            style={{
                                flex: 1,
                                padding: rexTechTheme.spacing.md,
                                backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                color: rexTechTheme.colors.neutral.darkGray,
                                borderRadius: rexTechTheme.borderRadius.medium,
                                cursor: 'pointer',
                                fontSize: rexTechTheme.typography.fontSize.regular,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                transition: 'all 0.2s ease',
                                ':hover': {
                                    backgroundColor: rexTechTheme.colors.neutral.gray
                                }
                            }}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleDelete}
                            style={{
                                flex: 1,
                                padding: rexTechTheme.spacing.md,
                                backgroundColor: rexTechTheme.colors.accent.orange,
                                border: 'none',
                                color: rexTechTheme.colors.neutral.white,
                                borderRadius: rexTechTheme.borderRadius.medium,
                                cursor: 'pointer',
                                fontSize: rexTechTheme.typography.fontSize.regular,
                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                transition: 'all 0.2s ease',
                                ':hover': {
                                    backgroundColor: '#d35400',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 4px 12px rgba(230, 126, 34, 0.3)'
                                }
                            }}
                        >
                            <Trash2 size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                            Delete Request
                        </button>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Status Update Modal */}
            <Modal show={showStatusModal} onHide={() => setShowStatusModal(false)} centered>
                <Modal.Header closeButton style={{
                    borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    backgroundColor: rexTechTheme.colors.primary.lightest
                }}>
                    <Modal.Title style={{
                        color: rexTechTheme.colors.primary.dark,
                        fontWeight: rexTechTheme.typography.fontWeight.bold
                    }}>
                        <RefreshCw size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                        Update Status
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: rexTechTheme.spacing.xl }}>
                    {selectedRequest && (
                        <div>
                            <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
                                <div style={{
                                    color: rexTechTheme.colors.neutral.darkGray,
                                    marginBottom: rexTechTheme.spacing.sm
                                }}>
                                    Current status:
                                </div>
                                <div style={{ display: 'inline-block' }}>
                                    {getStatusBadge(selectedRequest.status)}
                                </div>
                            </div>

                            <div style={{ marginBottom: rexTechTheme.spacing.xl }}>
                                {(() => {
                                    const nextStatus = getNextStatus(selectedRequest.status);
                                    const confirmation = getStatusConfirmationMessage(selectedRequest.status, nextStatus);

                                    if (!nextStatus) {
                                        return (
                                            <div style={{
                                                textAlign: 'center',
                                                padding: rexTechTheme.spacing.xl,
                                                backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                                borderRadius: rexTechTheme.borderRadius.medium
                                            }}>
                                                <CheckCircle size={32} color={rexTechTheme.colors.accent.teal} />
                                                <div style={{
                                                    marginTop: rexTechTheme.spacing.md,
                                                    color: rexTechTheme.colors.neutral.darkGray
                                                }}>
                                                    This request has been completed (RECYCLED).
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (
                                        <>
                                            <div style={{
                                                textAlign: 'center',
                                                marginBottom: rexTechTheme.spacing.lg
                                            }}>
                                                <div style={{
                                                    color: rexTechTheme.colors.primary.dark,
                                                    fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                    marginBottom: rexTechTheme.spacing.sm,
                                                    fontSize: rexTechTheme.typography.fontSize.large
                                                }}>
                                                    {confirmation.title}
                                                </div>
                                                <div style={{
                                                    color: rexTechTheme.colors.neutral.darkGray,
                                                    lineHeight: 1.5
                                                }}>
                                                    {confirmation.message}
                                                </div>
                                            </div>

                                            {/* Status Progression */}
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                marginBottom: rexTechTheme.spacing.xl,
                                                position: 'relative'
                                            }}>
                                                {statusOptions.map((status, index) => {
                                                    const isCurrent = selectedRequest.status === status.value;
                                                    const isCompleted = statusOptions.findIndex(s => s.value === selectedRequest.status) >= index;
                                                    const isNext = status.value === nextStatus;

                                                    return (
                                                        <div key={status.value} style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            position: 'relative',
                                                            zIndex: 1
                                                        }}>
                                                            <div style={{
                                                                width: '40px',
                                                                height: '40px',
                                                                borderRadius: '50%',
                                                                backgroundColor: isCompleted
                                                                    ? (isCurrent ? status.color : rexTechTheme.colors.primary.main)
                                                                    : rexTechTheme.colors.neutral.gray,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                color: rexTechTheme.colors.neutral.white,
                                                                fontWeight: rexTechTheme.typography.fontWeight.bold,
                                                                marginBottom: rexTechTheme.spacing.xs,
                                                                border: `2px solid ${isCurrent ? status.color : 'transparent'}`
                                                            }}>
                                                                {isCompleted ? (
                                                                    <CheckCircle size={20} />
                                                                ) : (
                                                                    <span>{index + 1}</span>
                                                                )}
                                                            </div>
                                                            <div style={{
                                                                fontSize: rexTechTheme.typography.fontSize.xs,
                                                                fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                                color: isNext ? rexTechTheme.colors.primary.dark : rexTechTheme.colors.neutral.darkGray,
                                                                textAlign: 'center',
                                                                maxWidth: '80px'
                                                            }}>
                                                                {status.label}
                                                            </div>
                                                        </div>
                                                    );
                                                })}

                                                {/* Connecting line */}
                                                <div style={{
                                                    position: 'absolute',
                                                    top: '20px',
                                                    left: '20px',
                                                    right: '20px',
                                                    height: '2px',
                                                    backgroundColor: rexTechTheme.colors.neutral.gray,
                                                    zIndex: 0
                                                }}></div>
                                            </div>

                                            <div style={{
                                                display: 'flex',
                                                gap: rexTechTheme.spacing.md
                                            }}>
                                                <button
                                                    onClick={() => setShowStatusModal(false)}
                                                    style={{
                                                        flex: 1,
                                                        padding: rexTechTheme.spacing.md,
                                                        backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                                        border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                                        color: rexTechTheme.colors.neutral.darkGray,
                                                        borderRadius: rexTechTheme.borderRadius.medium,
                                                        cursor: 'pointer',
                                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                        transition: 'all 0.2s ease',
                                                        ':hover': {
                                                            backgroundColor: rexTechTheme.colors.neutral.gray
                                                        }
                                                    }}
                                                >
                                                    {confirmation.cancelText}
                                                </button>
                                                <button
                                                    onClick={() => updateStatus(nextStatus)}
                                                    style={{
                                                        flex: 1,
                                                        padding: rexTechTheme.spacing.md,
                                                        backgroundColor: rexTechTheme.colors.primary.main,
                                                        border: 'none',
                                                        color: rexTechTheme.colors.neutral.white,
                                                        borderRadius: rexTechTheme.borderRadius.medium,
                                                        cursor: 'pointer',
                                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                                        transition: 'all 0.2s ease',
                                                        ':hover': {
                                                            backgroundColor: rexTechTheme.colors.primary.dark,
                                                            transform: 'translateY(-2px)',
                                                            boxShadow: '0 4px 12px rgba(30, 132, 73, 0.3)'
                                                        }
                                                    }}
                                                >
                                                    {confirmation.confirmText}
                                                </button>
                                            </div>
                                        </>
                                    );
                                })()}
                            </div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>

            {/* Assign Collector Modal */}
            <Modal show={showAssignModal} onHide={() => setShowAssignModal(false)} centered size="lg">
                <Modal.Header closeButton style={{
                    borderBottom: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                    backgroundColor: rexTechTheme.colors.primary.lightest
                }}>
                    <Modal.Title style={{
                        color: rexTechTheme.colors.primary.dark,
                        fontWeight: rexTechTheme.typography.fontWeight.bold
                    }}>
                        <Users size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                        Assign Collector
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: rexTechTheme.spacing.xl }}>
                    {selectedRequest && (
                        <div>
                            <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
                                <div style={{
                                    color: rexTechTheme.colors.neutral.darkGray,
                                    marginBottom: rexTechTheme.spacing.md
                                }}>
                                    Select a collector and provide truck details for this request:
                                </div>

                                {/* Collector Selection */}
                                <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: rexTechTheme.spacing.xs,
                                        color: rexTechTheme.colors.primary.dark,
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold
                                    }}>
                                        Collector
                                        <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '4px' }}>*</span>
                                    </label>
                                    <select
                                        value={selectedCollectorId}
                                        onChange={(e) => setSelectedCollectorId(e.target.value)}
                                        style={{
                                            width: '100%',
                                            padding: rexTechTheme.spacing.md,
                                            border: `2px solid ${assignErrors.collectorId ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            backgroundColor: rexTechTheme.colors.neutral.white,
                                            outline: 'none',
                                            ':focus': {
                                                borderColor: rexTechTheme.colors.primary.main
                                            }
                                        }}
                                    >
                                        <option value="">Select a collector</option>
                                        {collectors.map(collector => (
                                            <option key={collector.companyId} value={collector.companyId}>
                                                {collector.name}
                                            </option>
                                        ))}
                                    </select>
                                    {assignErrors.collectorId && (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: rexTechTheme.spacing.xs,
                                            marginTop: rexTechTheme.spacing.xs,
                                            color: rexTechTheme.colors.accent.orange,
                                            fontSize: rexTechTheme.typography.fontSize.small
                                        }}>
                                            <AlertCircle size={14} />
                                            <span>{assignErrors.collectorId}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Truck Reference Number */}
                                <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: rexTechTheme.spacing.xs,
                                        color: rexTechTheme.colors.primary.dark,
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: rexTechTheme.spacing.sm
                                    }}>
                                        <Key size={16} />
                                        Truck Reference Number
                                        <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '4px' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={truckReferenceNumber}
                                        onChange={(e) => setTruckReferenceNumber(e.target.value)}
                                        placeholder="Enter truck reference number"
                                        maxLength="50"
                                        style={{
                                            width: '100%',
                                            padding: rexTechTheme.spacing.md,
                                            border: `2px solid ${assignErrors.truckReferenceNumber ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            outline: 'none',
                                            ':focus': {
                                                borderColor: rexTechTheme.colors.primary.main,
                                                boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                            }
                                        }}
                                    />
                                    {assignErrors.truckReferenceNumber && (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: rexTechTheme.spacing.xs,
                                            marginTop: rexTechTheme.spacing.xs,
                                            color: rexTechTheme.colors.accent.orange,
                                            fontSize: rexTechTheme.typography.fontSize.small
                                        }}>
                                            <AlertCircle size={14} />
                                            <span>{assignErrors.truckReferenceNumber}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Seal Number */}
                                <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: rexTechTheme.spacing.xs,
                                        color: rexTechTheme.colors.primary.dark,
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: rexTechTheme.spacing.sm
                                    }}>
                                        <Hash size={16} />
                                        Seal Number
                                        <span style={{ color: rexTechTheme.colors.accent.orange, marginLeft: '4px' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={sealNumber}
                                        onChange={(e) => setSealNumber(e.target.value)}
                                        placeholder="Enter seal number"
                                        maxLength="50"
                                        style={{
                                            width: '100%',
                                            padding: rexTechTheme.spacing.md,
                                            border: `2px solid ${assignErrors.sealNumber ? rexTechTheme.colors.accent.orange : rexTechTheme.colors.neutral.gray}`,
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            outline: 'none',
                                            ':focus': {
                                                borderColor: rexTechTheme.colors.primary.main,
                                                boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                            }
                                        }}
                                    />
                                    {assignErrors.sealNumber && (
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: rexTechTheme.spacing.xs,
                                            marginTop: rexTechTheme.spacing.xs,
                                            color: rexTechTheme.colors.accent.orange,
                                            fontSize: rexTechTheme.typography.fontSize.small
                                        }}>
                                            <AlertCircle size={14} />
                                            <span>{assignErrors.sealNumber}</span>
                                        </div>
                                    )}
                                </div>

                                {/* Notes (Optional) */}
                                <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: rexTechTheme.spacing.xs,
                                        color: rexTechTheme.colors.primary.dark,
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: rexTechTheme.spacing.sm
                                    }}>
                                        <FileText size={16} />
                                        Notes (Optional)
                                    </label>
                                    <textarea
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        placeholder="Enter any additional notes or instructions for the collector..."
                                        rows="3"
                                        style={{
                                            width: '100%',
                                            padding: rexTechTheme.spacing.md,
                                            border: `2px solid ${rexTechTheme.colors.neutral.gray}`,
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            outline: 'none',
                                            ':focus': {
                                                borderColor: rexTechTheme.colors.primary.main,
                                                boxShadow: `0 0 0 3px ${rexTechTheme.colors.primary.lightest}`
                                            },
                                            resize: 'vertical'
                                        }}
                                        maxLength="500"
                                    />
                                    <div style={{
                                        marginTop: rexTechTheme.spacing.xs,
                                        fontSize: rexTechTheme.typography.fontSize.xs,
                                        color: rexTechTheme.colors.neutral.darkGray,
                                        textAlign: 'right'
                                    }}>
                                        {notes.length}/500 characters
                                    </div>
                                </div>

                                {/* Image Upload (Optional) */}
                                <div style={{ marginBottom: rexTechTheme.spacing.lg }}>
                                    <label style={{
                                        display: 'block',
                                        marginBottom: rexTechTheme.spacing.xs,
                                        color: rexTechTheme.colors.primary.dark,
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold
                                    }}>
                                        Upload Images (Optional)
                                    </label>

                                    {/* Image Upload Area */}
                                    <div
                                        onClick={() => document.getElementById('image-upload').click()}
                                        style={{
                                            border: `2px dashed ${rexTechTheme.colors.neutral.gray}`,
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            padding: rexTechTheme.spacing.xl,
                                            textAlign: 'center',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s ease',
                                            backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                            marginBottom: rexTechTheme.spacing.md,
                                            ':hover': {
                                                borderColor: rexTechTheme.colors.primary.main,
                                                backgroundColor: rexTechTheme.colors.primary.lightest
                                            }
                                        }}
                                    >
                                        <div style={{
                                            color: rexTechTheme.colors.neutral.darkGray,
                                            marginBottom: rexTechTheme.spacing.sm
                                        }}>
                                            <Plus size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
                                            <div>Click to upload images</div>
                                            <div style={{
                                                fontSize: rexTechTheme.typography.fontSize.small,
                                                color: rexTechTheme.colors.neutral.gray,
                                                marginTop: rexTechTheme.spacing.xs
                                            }}>
                                                JPG, PNG, GIF up to 5MB
                                            </div>
                                        </div>
                                    </div>

                                    <input
                                        id="image-upload"
                                        type="file"
                                        multiple
                                        accept="image/jpeg,image/jpg,image/png,image/gif"
                                        onChange={handleImageUpload}
                                        style={{ display: 'none' }}
                                    />

                                    {/* Selected Images Preview */}
                                    {selectedImages.length > 0 && (
                                        <div style={{
                                            marginTop: rexTechTheme.spacing.md
                                        }}>
                                            <div style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                alignItems: 'center',
                                                marginBottom: rexTechTheme.spacing.sm
                                            }}>
                                                <span style={{
                                                    color: rexTechTheme.colors.neutral.darkGray,
                                                    fontSize: rexTechTheme.typography.fontSize.small,
                                                    fontWeight: rexTechTheme.typography.fontWeight.medium
                                                }}>
                                                    {selectedImages.length} image(s) selected
                                                </span>
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedImages([])}
                                                    style={{
                                                        padding: `${rexTechTheme.spacing.xs} ${rexTechTheme.spacing.sm}`,
                                                        backgroundColor: rexTechTheme.colors.accent.orange + '20',
                                                        border: `1px solid ${rexTechTheme.colors.accent.orange}`,
                                                        color: rexTechTheme.colors.accent.orange,
                                                        borderRadius: rexTechTheme.borderRadius.small,
                                                        fontSize: rexTechTheme.typography.fontSize.xs,
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        ':hover': {
                                                            backgroundColor: rexTechTheme.colors.accent.orange,
                                                            color: rexTechTheme.colors.neutral.white
                                                        }
                                                    }}
                                                >
                                                    Clear All
                                                </button>
                                            </div>

                                            <div style={{
                                                display: 'grid',
                                                gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
                                                gap: rexTechTheme.spacing.sm,
                                                maxHeight: '200px',
                                                overflowY: 'auto',
                                                padding: rexTechTheme.spacing.sm,
                                                border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                                borderRadius: rexTechTheme.borderRadius.medium,
                                                backgroundColor: rexTechTheme.colors.neutral.white
                                            }}>
                                                {selectedImages.map((image, index) => (
                                                    <div
                                                        key={index}
                                                        style={{
                                                            position: 'relative',
                                                            borderRadius: rexTechTheme.borderRadius.small,
                                                            overflow: 'hidden',
                                                            border: `1px solid ${rexTechTheme.colors.neutral.gray}`
                                                        }}
                                                    >
                                                        <div style={{
                                                            width: '100%',
                                                            height: '80px',
                                                            backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            overflow: 'hidden'
                                                        }}>
                                                            {image.type.startsWith('image/') ? (
                                                                <img
                                                                    src={URL.createObjectURL(image)}
                                                                    alt={`Preview ${index + 1}`}
                                                                    style={{
                                                                        width: '100%',
                                                                        height: '100%',
                                                                        objectFit: 'cover'
                                                                    }}
                                                                />
                                                            ) : (
                                                                <FileText size={24} color={rexTechTheme.colors.neutral.gray} />
                                                            )}
                                                        </div>
                                                        <div style={{
                                                            padding: rexTechTheme.spacing.xs,
                                                            fontSize: rexTechTheme.typography.fontSize.xs,
                                                            color: rexTechTheme.colors.neutral.darkGray,
                                                            whiteSpace: 'nowrap',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            backgroundColor: rexTechTheme.colors.neutral.white
                                                        }}>
                                                            {image.name}
                                                        </div>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeImage(index)}
                                                            style={{
                                                                position: 'absolute',
                                                                top: '4px',
                                                                right: '4px',
                                                                width: '20px',
                                                                height: '20px',
                                                                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                                                                border: 'none',
                                                                borderRadius: '50%',
                                                                color: rexTechTheme.colors.neutral.white,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'pointer',
                                                                fontSize: '10px',
                                                                transition: 'all 0.2s ease',
                                                                ':hover': {
                                                                    backgroundColor: rexTechTheme.colors.accent.orange
                                                                }
                                                            }}
                                                        >
                                                            ×
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                gap: rexTechTheme.spacing.md
                            }}>
                                <button
                                    onClick={unassignCollector}
                                    disabled={btnLoading || !selectedRequest.collectorId}
                                    style={{
                                        padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                        backgroundColor: !selectedRequest.collectorId ? rexTechTheme.colors.neutral.lightGray : rexTechTheme.colors.accent.orange,
                                        border: `1px solid ${!selectedRequest.collectorId ? rexTechTheme.colors.neutral.gray : rexTechTheme.colors.accent.orange}`,
                                        color: !selectedRequest.collectorId ? rexTechTheme.colors.neutral.gray : rexTechTheme.colors.neutral.white,
                                        borderRadius: rexTechTheme.borderRadius.medium,
                                        cursor: !selectedRequest.collectorId || btnLoading ? 'not-allowed' : 'pointer',
                                        fontSize: rexTechTheme.typography.fontSize.regular,
                                        fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                        transition: 'all 0.2s ease',
                                        opacity: btnLoading ? 0.7 : 1,
                                        ':hover': (!selectedRequest.collectorId || btnLoading) ? {} : {
                                            backgroundColor: '#d35400',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 4px 12px rgba(230, 126, 34, 0.3)'
                                        }
                                    }}
                                >
                                    Unassign Collector
                                </button>

                                <div style={{ display: 'flex', gap: rexTechTheme.spacing.md }}>
                                    <button
                                        onClick={() => {
                                            setShowAssignModal(false);
                                            setSelectedImages([]);
                                        }}
                                        disabled={btnLoading}
                                        style={{
                                            padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                            backgroundColor: rexTechTheme.colors.neutral.lightGray,
                                            border: `1px solid ${rexTechTheme.colors.neutral.gray}`,
                                            color: rexTechTheme.colors.neutral.darkGray,
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            cursor: btnLoading ? 'not-allowed' : 'pointer',
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                            transition: 'all 0.2s ease',
                                            opacity: btnLoading ? 0.7 : 1,
                                            ':hover': btnLoading ? {} : {
                                                backgroundColor: rexTechTheme.colors.neutral.gray
                                            }
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={assignCollector}
                                        disabled={btnLoading}
                                        style={{
                                            padding: `${rexTechTheme.spacing.md} ${rexTechTheme.spacing.xl}`,
                                            background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.main} 0%, ${rexTechTheme.colors.primary.light} 100%)`,
                                            color: rexTechTheme.colors.neutral.white,
                                            border: 'none',
                                            borderRadius: rexTechTheme.borderRadius.medium,
                                            fontSize: rexTechTheme.typography.fontSize.regular,
                                            fontWeight: rexTechTheme.typography.fontWeight.semiBold,
                                            cursor: btnLoading ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.3s ease',
                                            opacity: btnLoading ? 0.7 : 1,
                                            boxShadow: '0 4px 15px rgba(30, 132, 73, 0.3)',
                                            ':hover': btnLoading ? {} : {
                                                background: `linear-gradient(135deg, ${rexTechTheme.colors.primary.dark} 0%, ${rexTechTheme.colors.primary.main} 100%)`,
                                                transform: 'translateY(-2px)',
                                                boxShadow: '0 6px 20px rgba(30, 132, 73, 0.4)'
                                            }
                                        }}
                                    >
                                        {btnLoading ? (
                                            <>
                                                <div style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    border: `2px solid ${rexTechTheme.colors.neutral.white}`,
                                                    borderTopColor: 'transparent',
                                                    borderRadius: '50%',
                                                    animation: 'spin 1s linear infinite',
                                                    display: 'inline-block',
                                                    marginRight: '8px',
                                                    verticalAlign: 'middle'
                                                }} />
                                                Assigning...
                                            </>
                                        ) : (
                                            <>
                                                <CheckCircle size={18} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                                Assign Collector
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default AdminRequests;